/**
 * CardPropertyList — the CardDetailDrawer's first-principles property list:
 * Status, Blocked reason, Snooze until, List/lane, Start → Due dates,
 * Effort, Priority. Each row pairs a quiet [icon · label] with an inline
 * ghost control. (Action date + note lifted to ActionSpineSection — ARC-4.)
 *
 * Extracted verbatim from CardDetailDrawer.tsx (operator 500-line ceiling).
 * Every onChange/onBlur write is a 1:1 move; state + handlers arrive via
 * the `editing` hook object.
 */
import { Calendar, Clock, Flag, ShieldAlert } from 'lucide-react'
import type { Card, CardStatus, Lane } from '../../../types'
import { DateField } from '../../DateField'
import {
  GHOST_CONTROL,
  PRIORITY_OPTIONS,
  PropRow,
  STATUS_META,
  STATUS_OPTIONS,
} from '../drawerChrome'
import type { DrawerEditing } from '../drawerHookTypes'

export interface CardPropertyListProps {
  card: Card
  lanes: Lane[]
  editing: DrawerEditing
}

export function CardPropertyList({ card, lanes, editing }: CardPropertyListProps) {
  const {
    status, setStatus, busyKey, patchCard,
    blockedReason, setBlockedReason, dirtyFieldsRef,
    snoozedUntil, setSnoozedUntil,
    startDate, setStartDate, dueDate, setDueDate,
    effortDays, setEffortDays, priority, setPriority, handleMove,
  } = editing

  return (
    <section className="divide-y divide-neutral-100 rounded-xl border border-neutral-200/70 bg-white px-3.5 dark:divide-neutral-800/70 dark:border-neutral-800 dark:bg-neutral-900/30">
      {/* Status */}
      <PropRow icon={<Clock className="h-3.5 w-3.5" />} label="Status">
        <span className={`mr-2 h-2 w-2 shrink-0 rounded-full ${STATUS_META[status]?.dot ?? 'bg-neutral-400'}`} />
        <select
          value={status}
          onChange={(e) => {
            const next = e.target.value as CardStatus
            setStatus(next)
            if (next !== card.status) {
              void patchCard('status', { status: next } as never)
            }
          }}
          disabled={busyKey === 'status'}
          className={`${GHOST_CONTROL} -ml-2 font-medium ${STATUS_META[status]?.text ?? ''}`}
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
      </PropRow>

      {/* Blocked reason — only meaningful while BLOCKED. Patches on blur. */}
      {status === 'BLOCKED' && (
        <PropRow icon={<ShieldAlert className="h-3.5 w-3.5" />} label="Blocked reason">
          <input
            type="text"
            value={blockedReason}
            placeholder="Why is this blocked?"
            aria-label="Blocked reason"
            onChange={(e) => {
              dirtyFieldsRef.current.add('blockedReason')
              setBlockedReason(e.target.value)
            }}
            onBlur={() => {
              const next = blockedReason.trim()
              if (next !== (card.blocked_reason ?? '')) {
                void patchCard('blocked_reason', { blocked_reason: next } as never)
              }
            }}
            disabled={busyKey === 'blocked_reason'}
            className={`${GHOST_CONTROL} -ml-2 w-full`}
          />
        </PropRow>
      )}

      {/* Snooze until — only shown while SNOOZED. Day granularity. */}
      {status === 'SNOOZED' && (
        <PropRow icon={<Clock className="h-3.5 w-3.5" />} label="Snooze until">
          <DateField
            ariaLabel="Snooze until"
            placeholder="Pick a date"
            value={snoozedUntil ? snoozedUntil.slice(0, 10) : null}
            onChange={(iso) => {
              setSnoozedUntil(iso)
              const current = card.snoozed_until ? card.snoozed_until.slice(0, 10) : null
              if (iso !== current) {
                void patchCard('snoozed_until', { snoozed_until: iso } as never)
              }
            }}
            className={`${GHOST_CONTROL} -ml-2 w-[150px]`}
          />
        </PropRow>
      )}

      {/* List / lane */}
      <PropRow icon={<Flag className="h-3.5 w-3.5" />} label="List">
        <select
          value={card.lane}
          onChange={(e) => void handleMove(e.target.value)}
          disabled={busyKey === 'lane'}
          className={`${GHOST_CONTROL} -ml-2 w-full`}
        >
          {lanes
            .filter((l) => !l.archived)
            .sort((a, b) => a.position - b.position)
            .map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
        </select>
      </PropRow>

      {/* Action date + note moved to the ACTION spine at the top of the Card
          tab (ARC-4 U02 — ActionSpineSection). Same editing state + patch
          path; one write home, no duplicate row here. */}

      {/* Dates — start → due on one line (PR 7 Gantt timeline). */}
      <PropRow icon={<Calendar className="h-3.5 w-3.5" />} label="Dates">
        <DateField
          ariaLabel="Start date"
          placeholder="Start"
          value={startDate || null}
          onChange={(iso) => {
            setStartDate(iso ?? '')
            if (iso !== (card.start_date ?? null)) {
              void patchCard('start_date', { start_date: iso } as never)
            }
          }}
          className={`${GHOST_CONTROL} -ml-2 w-[150px]`}
        />
        <span className="shrink-0 px-0.5 text-neutral-300 dark:text-neutral-600">→</span>
        <DateField
          ariaLabel="Due date"
          placeholder="Due"
          value={dueDate || null}
          onChange={(iso) => {
            setDueDate(iso ?? '')
            if (iso !== (card.due_date ?? null)) {
              void patchCard('due_date', { due_date: iso } as never)
            }
          }}
          className={`${GHOST_CONTROL} w-[150px]`}
        />
      </PropRow>

      {/* Effort (days) */}
      <PropRow icon={<Clock className="h-3.5 w-3.5" />} label="Effort">
        <input
          type="number"
          min={0}
          step={1}
          value={effortDays}
          placeholder="-"
          onChange={(e) => setEffortDays(e.target.value)}
          onBlur={() => {
            const trimmed = effortDays.trim()
            const next = trimmed === '' ? null : Math.max(0, parseInt(trimmed, 10) || 0)
            if (next !== (card.effort_days ?? null)) {
              void patchCard('effort_days', { effort_days: next } as never)
            }
          }}
          className={`${GHOST_CONTROL} -ml-2 w-16`}
        />
        <span className="text-[12px] text-neutral-400">days</span>
      </PropRow>

      {/* Priority */}
      <PropRow icon={<ShieldAlert className="h-3.5 w-3.5" />} label="Priority">
        <select
          value={priority}
          onChange={(e) => {
            const next = parseInt(e.target.value, 10)
            setPriority(next)
            if (next !== card.priority) {
              void patchCard('priority', { priority: next })
            }
          }}
          disabled={busyKey === 'priority'}
          className={`${GHOST_CONTROL} -ml-2 font-medium ${PRIORITY_OPTIONS.find((p) => p.value === priority)?.cls ?? ''}`}
        >
          {PRIORITY_OPTIONS.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </select>
      </PropRow>
    </section>
  )
}
